import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import * as Rx from 'rxjs';
import { tap, map } from 'rxjs/operators';
import { AnonymousUserService } from '../user/anonymousUser/anonymous-user.service';
import { AppSettingService, AppUtilityService } from '../app.service';
import { CacheService } from '../utility/cache.service';
import { UserInfoService } from '../user/user-info.service';

export enum ESocialSourceType {
	Map = 1,
	Resource = 2,
	CollegeScorecard = 3,
	NCES = 4,
	Cyber = 5,
	CareerOneStopTraining = 6,
	CareerOneStopOrg = 7
}

export enum ESocialType {
	Unknown = 0,
	CSCCollege = 1,
	CSCProgram = 2,
	// Career
	CareerCluster = 4,
	CareerPathway = 5,
	Occupation = 6,
	// K12
	K12Grade = 12,
	K12Subject = 13,
	K12Strand = 14,
	K12Standard = 15,
	K12Benchmark = 16,
	K12SubBenchmark = 17,
	K12Resource = 18,
	// College
	CollegeCluster = 22,
	CollegeProgram = 23,
	CollegeCourse = 24,
	// CTE
	CTECluster = 32,
	CTEPathway = 33,
	CTEStrand = 34,
	CTEStandard = 35,
	CTEPerformanceIndicator = 36,
	//Certification 
	CertificationCluster = 42,
	CertificationPathway = 43,
	Certificate = 45,
	CertificateTraining = 47,
	CertificateOrg = 46,
	//Military
	Military = 54,
	// Cyber
	CyberPodcast = 247,
	CyberPodcastEpisode = 248,
	CyberEvent = 249,
	CyberArticle = 250,
	// others
	Resource = 255
}

export enum ESocialIconType {
	Follow = 1,
	Save = 2,    // use for Resource
	Favorite = 3 // Favorite is used by podcast, but we save as follow
}

export enum ESocialIconStyle {
	Icon = 1,
	Button = 2
}

export type TMyFollowingSort = "AToZ" | "ZToA" | "Chronological";

export interface ISocialSearch {
	TypeIds: number[],
	Keyword?: string,
	SortBy: TMyFollowingSort,
	UserId?: string,
	Limit: number,
	PreviousTime?: Date,
	PreviousName?: string
}

export interface IOccupation {
	OccupationCode: string,
	OccupationName: string,
	MapObjectId: string,
	PathwayName: string,
	ClusterName: string,
	IsBrightOutlook: boolean,
	IsGreen: boolean,
	IsHot: boolean,
	Education: {
		UnderHighSchoolPercent: number,
		HighSchoolDiplomaPercent: number,
		AssociateDegreePercent: number,
		BachelorDegreePercent: number,
		MasterDegreePercent: number,
		DoctoralDegreePercent: number,
		PostBaccalaureateCertificatePercent: number,
		PostSecondaryCertificatePercent: number,
		SomeCollegeNoDegreePercent: number,
		PostDoctoralTrainingPercent: number,
		PostMasterCertificatePercent: number,
		ProfessionalDegreePercent: number
	},
	CurrentEmployment: string,
	ProjectedEmploymentGrowthPercent: string,
	ProjectedEmploymentGrowthAverage: string,
	ProjectedJobOpenings: string,
	AverageAnnualSalary: string,
	HeroImage: string,
	MapInstances: [
		{
			ClusterId: string,
			ClusterHeroImage: string,
			ClusterName: string,
			MapObjectId: string,
			PathwayId: string,
			PathwayHeroImage: string,
			PathwayName: string,
			ClusterIcon: string
		}
	],
	LevelIconClusters: string [],
	Journeyable: boolean,
	HasLearnMore: boolean,
	CanSocial: boolean,
	DepthLevel: number,
	TerritoryId: number,
	Description: string,
	IsAvailable: boolean,
	ObjectKey: string,
	ObjectName: string
}

/* SocialObjectAPIService migrated from informAPI.js -v129 */
@Injectable({
	providedIn: 'root'
})
export class SocialObjectAPIService {
	socialStatushanged: any = null;

	constructor(
		private anonymousSrv: AnonymousUserService,
		private cacheSrv: CacheService,
		private userInfo: UserInfoService,
		private http: HttpClient
	) {
		//this.getUserAllSocialObjects().subscribe();
		this.socialStatushanged = new Rx.BehaviorSubject(1);
	}

	private userSOCache: any = null;
	private url = AppSettingService.appConfig.JourneyAPIUrl + "SocialObject";

	initUserSocialObjects(): Promise<any> {
		if (this.userInfo == null || this.userInfo.isAnonymous()) {
			return new Promise((resolve) => {
				resolve([]);
			});
		}
		else {
			// ensure we do not throw error
			return Rx.firstValueFrom(this.getUserAllSocialObjects())
				.catch((error: any) => {
					console.log("initUserSocialObjects error: ", error);
				}).then(() => { });
		}
	}

	getUserAllSocialObjects(): Rx.Observable<any> {
		if (this.userInfo.isAnonymous()) {
			return Rx.of([]);
		}
		else if (this.userSOCache != null) {
			return Rx.of(this.userSOCache);
		}
		else {
			return this.http.get<any>(
				this.url + "/",
				{
					withCredentials: true,
					headers: { 'Authorization': this.userInfo.authHeader(false) }
				}
			).pipe(
				tap(
					response => {
						this.userSOCache = response;
					}
				)
			);
		}
	}

	getSocialObjectType(): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + "/Type",
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	isFollowing(sourceId: string, typeId: string, objectKey: string): Rx.Observable<boolean> {
		sourceId == null ? '' : sourceId.trim();
		typeId == null ? '' : typeId.trim();
		objectKey == null ? '' : objectKey.trim();

		if (sourceId === '' && typeId === '') {
			return Rx.throwError(() => { 'please provide either sourceId or typeId' });
		}
		else if (objectKey === '') {
			return Rx.of(false);
		}
		else {
			return this.getUserAllSocialObjects().pipe(
				map(
					response => {
						let o = null;
						if (typeId !== '' && typeId !== '0') {
							o = response.filter((x: any) => { return x.TypeId === typeId && x.ObjectKey === objectKey; });
						}
						else {
							o = response.filter((x: any) => { return x.SourceId === sourceId && x.ObjectKey === objectKey; });
						}
						if (o != null && o.length > 0) {
							return true;
						}
						else {
							return false;
						}
					}
				)
			)
		}
	}

	saveUserSocialObject(socialObject: any): Rx.Observable<any> {
		if (this.userInfo.isAnonymous()) {
			this.anonymousSrv.openSignupLogin("login", "", true, "", null);
			return Rx.throwError(() => "Need login");
		}
		else {
			return this.http.post<any>(
				this.url + "/Save",
				socialObject,
				{
					withCredentials: true,
					headers: { 'Authorization': this.userInfo.authHeader(true) }
				}
			).pipe(
				map(
					response => {
						this.userSOCache = response;
						this.socialStatushanged.next(this.socialStatushanged.getValue() + 1);
						return { data: "done" };
					}
				)
			);
		}
	}

	saveUserSocialObjectById(sourceId: string, objectTypeId: string, objectKey: string, follow: boolean): Rx.Observable<any> {
		sourceId = sourceId == null ? "" : sourceId.toString().trim();
		objectTypeId = objectTypeId == null ? "" : objectTypeId.toString().trim();
		objectKey = objectKey == null ? "" : objectKey.toString().trim();

		if (sourceId === '' || objectTypeId === '' || objectKey === '') {
			return Rx.throwError(() => { "source, type, and object key are required" });
		}

		if (this.userInfo.isAnonymous()) {
			this.anonymousSrv.openSignupLogin("login", "", true, "", null);
			return Rx.throwError(() => { "Need login" });
		}
		else {
			return this.http.get<any>(
				this.url + '/Save/' + encodeURIComponent(sourceId) + '/' + encodeURIComponent(objectTypeId) + '/' + encodeURIComponent(objectKey) + '/' + follow.toString(),
				{
					withCredentials: true,
					headers: { 'Authorization': this.userInfo.authHeader(false) }
				}
			).pipe(
				map(
					response => {
						this.userSOCache = response;
						this.socialStatushanged.next(this.socialStatushanged.getValue() + 1);
						return { data: "done" };
					}
				)
			);
		}
	}

	searchUserObjects(filter: ISocialSearch): Rx.Observable<any> {
		let formData: FormData = new FormData();
		AppUtilityService.objectToFormData(filter, formData, null);
		return this.http.post<any>(
			this.url + "/Search",
			formData,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	countSocialObjectFollow(sourceId: string, objectTypeId: string, objectKey: string, reload: boolean): Rx.Observable<any> {
		let l: string = this.url + "/Count/Follow/" + encodeURIComponent(sourceId) + "/" + encodeURIComponent(objectTypeId) + "/" + encodeURIComponent(objectKey);
		if (reload) {
			this.cacheSrv.remove(l);
		}
		return this.cacheSrv.get(false, l);
	}

	//get social status by person
	getActivityObjectList(userId: string, objectType: number): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + "/ObjectList?UserId=" + userId + "&ObjectType=" + objectType.toString(),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	countMyFollowing(): Rx.Observable<number> {
		return this.getUserAllSocialObjects().pipe(
			map(
				response => {
					if (response != null && response.length > 0) {
						return response.filter((x: any) => { return x.IsCard === true; }).length;
					}
					else {
						return 0;
					}
				}
			)
		)
	}
}
